import { useState } from 'react';

import { api, ApiError } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

import { useConversationLifecycle } from './use-conversation-lifecycle';

/**
 * useCloseConversationFlow — fluxo "Encerrar" da tela da Conversa (Fatia F +
 * ADR-150 follow-up), EXTRAÍDO verbatim do ConversationHeader na Fase 1
 * «um cérebro só» (T9): o chip `close` da sugestão unificada precisa abrir o
 * MESMO fluxo a partir do Composer, sem duplicar a lógica de submit
 * (lifecycle.close + blockers + follow-up + toasts).
 *
 * Consumidores: ConversationHeader (botão Encerrar) + Composer (chip `close`).
 * Cada consumidor renderiza sua própria `ConversationCloseModal` com estas
 * alças — o comportamento do submit é UM só.
 */
export function useCloseConversationFlow(conversationId: string): {
  closeOpen: boolean;
  openClose: () => void;
  /** Fecha o modal + limpa o erro de blocker (onClose do modal). */
  dismissClose: () => void;
  closeBlocker: string | undefined;
  handleCloseSubmit: (
    body: Parameters<ReturnType<typeof useConversationLifecycle>['close']>[0] & {
      followup?: { waitDays: number; content: string; mode: 'H_a' | 'H_b' | 'H_c' };
    },
  ) => Promise<void>;
  isClosing: boolean;
} {
  // Fatia F — wiring da ação Encerrar (modal estruturado + lifecycle.close).
  const lifecycle = useConversationLifecycle(conversationId);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeBlocker, setCloseBlocker] = useState<string | undefined>(undefined);

  const handleCloseSubmit = async (
    body: Parameters<typeof lifecycle.close>[0] & {
      followup?: { waitDays: number; content: string; mode: 'H_a' | 'H_b' | 'H_c' };
    },
  ): Promise<void> => {
    setCloseBlocker(undefined);
    const { followup, ...closeBody } = body;
    try {
      const result = await lifecycle.close(closeBody);
      setCloseOpen(false);
      useToastStore.getState().push({
        variant: result.warnings.length > 0 ? 'info' : 'success',
        title: 'Conversa encerrada',
        description:
          result.warnings.length > 0 ? result.warnings.join(' ') : 'Resultado registrado.',
        durationMs: result.warnings.length > 0 ? 6000 : 3500,
      });
      // ADR-150 — agenda o follow-up DEPOIS do close bem-sucedido (parcial
      // honesto: o encerramento já valeu; falha do agendamento vira toast).
      if (followup !== undefined) {
        try {
          const scheduled = await api.scheduleManualFollowup({
            conversationId,
            waitDays: followup.waitDays,
            content: followup.content,
            mode: followup.mode,
          });
          useToastStore.getState().push({
            variant: 'success',
            title: 'Follow-up agendado',
            description: `Retomada programada para ${new Date(scheduled.scheduledFor).toLocaleDateString('pt-BR')}.`,
            durationMs: 4000,
          });
        } catch (followupErr) {
          useToastStore.getState().push({
            variant: 'error',
            title: 'Conversa encerrada, mas o follow-up não foi agendado',
            description:
              followupErr instanceof Error ? followupErr.message : 'Tente novamente pelo T8.',
            durationMs: 8000,
          });
        }
      }
    } catch (err) {
      const errorBody =
        err instanceof ApiError
          ? (err.body as { code?: string; blockers?: { message: string }[] })
          : undefined;
      if (errorBody?.code === 'closure-blocked' && errorBody.blockers !== undefined) {
        setCloseBlocker(errorBody.blockers.map((b) => b.message).join(' '));
      } else {
        setCloseBlocker(
          err instanceof Error ? err.message : 'Não foi possível encerrar a conversa.',
        );
      }
    }
  };

  return {
    closeOpen,
    openClose: () => setCloseOpen(true),
    dismissClose: () => {
      setCloseOpen(false);
      setCloseBlocker(undefined);
    },
    closeBlocker,
    handleCloseSubmit,
    isClosing: lifecycle.isClosing,
  };
}
